// @flow
import React, { useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import slice from 'ramda/es/slice';
import append from 'ramda/es/append';
import isEmpty from 'ramda/es/isEmpty';
import classNames from 'classnames/bind';

import PlainFile from 'app/common/ui/PlainFile';
import ProgressBar from 'app/common/ui/ProgressBar';
import { api } from 'app/api';
import { type FilesType, type FileType } from 'app/common/api';
import config from 'app/config';
import OverflowedText from 'app/common/components/OveflowedText';
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link';

import FileView, { CrossButton } from '../FileView';
import styles from './AddFiles.scss';


const supportedFiles = 'PDF, JPEG, PNG, DOC, DOCX, XLS, XLSX';
const supportedAttachments = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const cx = classNames.bind(styles);

type Props = {
  disabled?: boolean,
  onAddFiles: (files: FilesType) => void,
  onRemoveFile: (id: string) => void,
  files: Array<FileType>,
}

const AddFiles = ({ disabled, onAddFiles, onRemoveFile, files }: Props) => {
  const { t } = useTranslation('statements');
  const [progress, setProgress] = useState(0);
  const [loadingFileName, setLoadingFileName] = useState('');
  const [exceedFiles, setExceedFiles] = useState([]);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const dropzone = useRef(null);


  const handleProgress = useCallback(({ loaded, total }) => {
    setProgress(Math.round((loaded * 100) / total));
  }, []);


  const handleOpenClick = useCallback(() => {
    if (dropzone.current) {
      dropzone.current.open();
    }
  }, []);

  const handleAddFiles = useCallback(async (acceptedFiles = []) => {
    if (acceptedFiles.length) {
      // берем сколько вмещается
      const fit = config.maxFiles - files.length;
      const len = (acceptedFiles.length < fit) ? acceptedFiles.length : fit;

      const actualFiles = slice(0, len, acceptedFiles);
      let submitedFiles = {};

      for await (const file of actualFiles) { // eslint-disable-line no-restricted-syntax
        setLoadingFileName(file.name);
        // $FlowFixMe
        const res = await api.submitFile(file, handleProgress);
        if (res) {
          setLoadingFileName('');
          submitedFiles = { ...submitedFiles, ...res };
        }
      }

      onAddFiles(submitedFiles);
    }
  }, [handleProgress, files, onAddFiles]);


  const handleRemoveFile = useCallback((e: SyntheticEvent<HTMLButtonElement>) => {
    const { id } = e.currentTarget;
    onRemoveFile(id);
  }, [onRemoveFile]);


  const handleAddInvalidFormatFile = useCallback((name: string) => {
    setInvalidFiles(append(name));
  }, []);

  const handleClearInvalidFormat = useCallback(() => {
    setInvalidFiles([]);
  }, []);

  const handleAddExceedSizeFile = useCallback((name: string) => {
    setExceedFiles(append(name));
  }, []);

  const handleClearExceedSize = useCallback(() => {
    setExceedFiles([]);
  }, []);

  return (
    <PlainFile
      id="files"
      label={t('create_statement_form_files_label')}
      comment={t('create_statement_form_files_comment', {
        supportedFiles,
        maxSize: Math.round(config.maxFileSize / 1050578),
        maxFiles: config.maxFiles,
      })}
      ref={dropzone}
      accept={supportedAttachments.join(', ')}
      maxSize={config.maxFileSize}
      onDrop={handleAddFiles}
      disablePreview
      disabled={disabled}
      onInvalidFormat={handleAddInvalidFormatFile}
      onExceedSize={handleAddExceedSizeFile}
    >
      {dragState => (
        <div>
          {files.map(({ id, name, thumb_url: urlThumb, url }) => (
            <FileView
              key={id}
              id={id}
              name={name}
              urlThumb={urlThumb}
              url={url}
              onCrossClick={handleRemoveFile}
            />
          ))}

          {loadingFileName
            && <div className={styles.loading}>
              <ProgressBar progress={progress} />
              <OverflowedText className={styles.loadingFileName}>
                {loadingFileName}
              </OverflowedText>
            </div>}

          {!isEmpty(exceedFiles)
            && <div className={styles.unAccepted}>
              {t('create_statement_form_error_unaccepted_size_message', { files: exceedFiles.join(', ') })}
              <CrossButton onCrossClick={handleClearExceedSize} />
            </div>}

          {!isEmpty(invalidFiles)
            && <div className={styles.unAccepted}>
              {t('create_statement_form_error_unaccepted_format_message', { files: invalidFiles.join(', ') })}
              <CrossButton onCrossClick={handleClearInvalidFormat} />
            </div>}

          {(files.length >= config.maxFiles || disabled)
            ? <div className={cx(styles.dropArea, { maxFiles: files.length >= config.maxFiles })}>
              <div>
                {!disabled
                  && t('create_statement_form_files_limit_exceeded')}
              </div>
            </div>
            : <div className={cx(styles.dropArea, { ...dragState })}>
              <div>
                <PlainButtonAsLink
                  onClick={handleOpenClick}
                  ariaLabel={t('create_statement_form_files_select_1')}
                >
                  {t('create_statement_form_files_select_1')}
                </PlainButtonAsLink>
                <span>{` ${t('create_statement_form_files_select_2')}`}</span>
              </div>
            </div>}
        </div>
      )}
    </PlainFile>
  );
};

export default AddFiles;
